import React from "react";

import styled from "styled-components";

import Icon from "shared/components/Icon";
import { Popover, PopoverContent } from "shared/components/Popover";
import Tooltip from "shared/components/Tooltip";
import { buildClassName } from "shared/utils/vega";

import StatusIcon, {
  LgStatusIcon,
  LgStatusTone,
  getStatusColor,
  lgStatusTone,
} from "../StatusIcon";
import { RationaleText } from "manager/components/vero1/DealReviewTab/styled";

// Cosign badge component
const CosignBadge = () => (
  <Popover
    trigger="hover"
    content={<PopoverContent>Cosign guarantee</PopoverContent>}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        backgroundColor: "#EFF6FF",
        borderRadius: "6px",
        padding: "2px 6px",
        fontSize: "11px",
        fontWeight: 600,
        color: "#2563EB",
        whiteSpace: "nowrap",
        position: "absolute",
        right: "-10px",
      }}
    >
      <Icon.CosignShieldIcon fill="#2563EB" width={17} height={23} />
    </span>
  </Popover>
);

// Helper function to get the appropriate popover title based on status
const getPopoverTitle = (status) => {
  if (status === "fail") {
    return "Requirements Not Met";
  }
  if (status === "warn") {
    return "Conditionally Failed Requirements";
  }
};

// Helper function to create popover content based on details format
const createPopoverContent = (details, status) => {
  if (!details) return null;

  const popoverTitle = getPopoverTitle(status);

  // Handle array of objects with title/description from getOutcomeDetailsFromApplicantData
  if (
    Array.isArray(details) &&
    details.length > 0 &&
    typeof details[0] === "object"
  ) {
    // Check if these are explanation objects with title/description
    if ("title" in details[0] || "description" in details[0]) {
      return (
        <div className="vega content popover">
          <h4
            style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              fontWeight: "500",
              color: getStatusColor(status),
            }}
          >
            {popoverTitle}
          </h4>
          <ul style={{ margin: 0, paddingLeft: "15px" }}>
            {details.map((item, idx) => (
              <li key={`detail-item-${item.title || idx}`}>
                {item.title && item.description ? (
                  <>
                    <strong>{item.title}:</strong> {item.description}
                  </>
                ) : (
                  item.title || item.description || ""
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }

  // Check if it's a single structured detail object
  const isStructuredDetails =
    typeof details === "object" && !Array.isArray(details);

  if (isStructuredDetails) {
    // Render structured details with title and description on a single line
    return (
      <div className="vega content popover">
        <h4
          style={{
            margin: "0 0 8px 0",
            fontSize: "14px",
            fontWeight: "500",
            color: getStatusColor(status),
          }}
        >
          {popoverTitle}
        </h4>
        <p
          style={{
            margin: "0",
            fontSize: "13px",
            lineHeight: "1.4",
          }}
        >
          {details.title && details.description ? (
            <>
              <strong>{details.title}:</strong> {details.description}
            </>
          ) : (
            details.title || details.description || ""
          )}
        </p>
      </div>
    );
  }

  if (Array.isArray(details) && details.length > 0) {
    // Render array of strings as bullet points
    return (
      <div className="vega content popover">
        <h4
          style={{
            margin: "0 0 8px 0",
            fontSize: "14px",
            fontWeight: "500",
            color: getStatusColor(status),
          }}
        >
          {popoverTitle}
        </h4>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          {details.map((item, idx) => (
            <li
              key={`detail-item-${
                typeof item === "string" ? item.substring(0, 10) : idx
              }`}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
};

const LG_CARD_BORDERS: Record<LgStatusTone, string> = {
  pass: "#05CE88",
  warn: "#E8A33D",
  fail: "#F0728C",
  pending: "#C6C2D4",
};

const LgCard = styled.div<{ $tone: LgStatusTone }>`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #edecf2;
  border-left: 3px solid ${({ $tone }) => LG_CARD_BORDERS[$tone]};
  border-radius: 6px;
  padding: 12px 13px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  background: #fff;
`;

const LgHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const LgTitle = styled.span`
  /* -1px: optical centering on the status circle (Work Sans ink sits low) */
  font: 600 16px/17px "Work Sans", sans-serif;
  transform: translateY(-1px);
  color: #2d2943;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const LgLine = styled.div`
  font: 400 14.5px/1.45 "Work Sans", sans-serif;
  color: #8b87a0;
  word-break: break-word;
`;

const ReviewLink = styled.button`
  font: 600 13px/1.4 "Work Sans", sans-serif;
  color: #524986;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  align-self: flex-start;
  margin-top: auto;
`;

const LumenEntryCard = ({
  title,
  label,
  status,
  details,
  cosignApproved,
  tooltipContent,
  lgTone = false,
  reviewInForce = false,
  onOpenReview,
}) => {
  const cardStatus = status;
  const hasDetails = !!details;
  const popoverContent = createPopoverContent(details, cardStatus);
  // Disable popover for now
  const disablePopover = true;
  const tooltipId = `income-tooltip-${title
    ?.replace(/\s+/g, "-")
    .toLowerCase()}`;

  const reviewLink = onOpenReview && (
    <ReviewLink
      type="button"
      data-testid="card-open-review"
      onClick={onOpenReview}
    >
      {"View VERO1 Changes \u2192"}
    </ReviewLink>
  );

  const considerCosignApprovedStatus =
    cosignApproved && title !== "ID Verification";
  const tooltipIcon = tooltipContent && (
    <Tooltip
      title={
        <div
          id={tooltipId}
          style={{ whiteSpace: "pre-line", lineHeight: "1.6" }}
        >
          {tooltipContent}
        </div>
      }
      trigger={"hover" as any}
      placement="top"
    >
      <span
        role="img"
        aria-label={`Income-to-rent requirement: ${tooltipContent}`}
        aria-describedby={tooltipId}
        tabIndex={0}
        style={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
          color: "#6B7280",
          flexShrink: 0,
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm1 12H7V7h2v5zm0-6H7V4h2v2z" />
        </svg>
      </span>
    </Tooltip>
  );

  if (lgTone) {
    return (
      <LgCard $tone={lgStatusTone(cardStatus)} data-status={cardStatus}>
        <LgHead>
          <LgStatusIcon status={cardStatus} />
          {title && <LgTitle>{title}</LgTitle>}
          {tooltipIcon}
          {considerCosignApprovedStatus && <CosignBadge />}
        </LgHead>
        {label &&
          String(label)
            .split("\n")
            .filter((line) => line.trim() !== "")
            .map((line) =>
              reviewInForce ? (
                <RationaleText key={line} data-testid="card-detail-line">
                  {line}
                </RationaleText>
              ) : (
                <LgLine key={line}>{line}</LgLine>
              )
            )}
        {reviewLink}
      </LgCard>
    );
  }

  const cardContent = (
    <div
      className={buildClassName([
        "vega flex gap small column card service",
        "status-bar",
        cardStatus,
      ])}
      style={{
        borderLeft: `3px solid ${getStatusColor(cardStatus)}`,
        cursor: hasDetails && !disablePopover ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        height: "auto",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: "16px",
        borderRadius: "12px",
        boxShadow:
          "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)",
        transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        className="vega flex gap small column"
        style={{ width: "100%", alignSelf: "flex-start" }}
      >
        <div
          className="vega flex gap small items-center"
          style={{ position: "relative" }}
        >
          <StatusIcon status={cardStatus} />
          {title && (
            <span
              className="vega title"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {title}
            </span>
          )}
          {tooltipIcon}
          {considerCosignApprovedStatus && <CosignBadge />}
        </div>
      </div>

      {label && (
        <div
          className="vega label"
          style={{
            overflow: "hidden",
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            width: "100%",
            marginTop: "8px",
          }}
        >
          {reviewInForce ? (
            <RationaleText as="div" data-testid="card-detail-line">
              {label}
            </RationaleText>
          ) : (
            label
          )}
        </div>
      )}
      {reviewLink && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: "auto",
            paddingTop: "8px",
          }}
        >
          {reviewLink}
        </div>
      )}
    </div>
  );

  // Disable popover for now

  return hasDetails && !disablePopover ? (
    <Popover content={popoverContent} trigger="hover" placement="right">
      {cardContent}
    </Popover>
  ) : (
    cardContent
  );
};

export default LumenEntryCard;
